import { createSlice } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import client, { authClient } from 'api/apiClient';
import { notify } from 'components/Common/Toast';
import { deletePropertyPath } from 'utils/helpers';
import { resetState } from 'redux/action/resetState';
import { UpdateUserDataPayload, UserInfo, UserState } from 'types/user';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';

export interface ResponseOverRide extends AxiosResponse {
    user?: UserInfo | null;
    responseData?: any;
    code?: number;
    message?: string;
    statusCode?: string;
}

const initialState: UserState = {
    userInfo: null,
    loading: false,
    error: null,
    updateInfoLoading: false,
    updateInfoError: null,
    defaultMerchant: null,
    defaultCompanyId: null,
    userResponse: null,
    address: null,
    logoutLoading: false,
};

const keysToRemoveIfEmpty = ['parishName', 'address.street2', 'catholicDirectoryAccountNumber', 'username'];
export const setMyPicture = createAsyncThunkWrapper<any, any>(
    'user/setMyPicture',
    async ({ formData }: { formData: any }, { dispatch }) => {
        const res: any = await client.post('/user/setMyPicture', formData, {
            'Content-Type': 'multipart/form-data',
        });
        notify('update picture successfully!', 'success');

        if (res.code === 200) {
            dispatch(fetchUserInfo());
        }
        return res;
    }
);

export const removeMyPicture = createAsyncThunkWrapper('user/removeMyPicture', async (_, { dispatch }) => {
    const res: any = await client.delete('/user/removeMyPicture');
    notify('Picture removed successfully!', 'success');

    if (res.code === 200) {
        dispatch(fetchUserInfo());
    }
    return res;
});

export const fetchUserInfo = () => async (dispatch: any) => {
    const { payload } = await dispatch(myUserInfo());
    if (!payload) {
        dispatch(logoutUser());
    }
};

export const myUserInfo = createAsyncThunkWrapper<ResponseOverRide>(
    'user/userInfo',
    async () => {
        const response = await client.get('/user/getMyUserInfo');
        return response;
    },
    { errorMessage: 'Error while fetching user info, Please contact support' }
);
//Change the keys named address1, address2, and zip to street1, street2, and zipcode, respectively,
//because the backend accepts street1, street2, and zipcode, and returns address1, address2, and zip.
const normalizeUserData = (userData: any, addressId?: number) => {
    const _userData = { ...userData };
    const { address1: street1, address2: street2, zip, ...rest } = _userData.address || {};
    const normalizedData = { ...userData, addressId, address: { ...rest, street1, street2, zipCode: zip } };
    // REMOVE THE KEYS THAT HAVE EMPTY, NULL OR UNDEFINED VALUES DUE TO BACKEND RESTRICTIONS
    keysToRemoveIfEmpty.forEach((key) => {
        if (!normalizedData?.[key]) {
            deletePropertyPath(normalizedData, key);
        }
    });
    return normalizedData;
};

export const userUpdateInfo = createAsyncThunkWrapper<any, UpdateUserDataPayload>(
    'user/userUpdateInfo',
    async (userData: UpdateUserDataPayload, { dispatch, getState }) => {
        const state: any = getState();
        const addressId = state?.user?.address?.id;
        const normalizedData = normalizeUserData(userData, addressId);

        const response: any = await client.post('/user/updateMyInfo', normalizedData);
        if (response.code === 200) {
            dispatch(myUserInfo());
            notify('Information updated successfully!', 'success');
        }
        return response;
    }
);

export const logoutUser = createAsyncThunkWrapper('user/logoutUser', async (_, { dispatch }) => {
    // Do not remove this try catch block as we need to logout the user even if logout api fails
    try {
        const res: any = await client.post('user/logout', {}, { hideToast: true });
        return res;
    } catch (e) {
        return e;
    } finally {
        authClient.clearTokens();
        client.clearTokens();
        dispatch(resetState());
        notify('Logged out successfully!', 'success');
    }
});
const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(myUserInfo.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(myUserInfo.fulfilled, (state, action) => {
            state.loading = false;
            if (!action.payload) return;
            const { user } = action.payload;
            if (user) {
                const { merchants, addresses } = user;
                if (merchants?.length) {
                    const defaultMerchant = merchants?.[0] || null;
                    if (defaultMerchant) {
                        state.defaultCompanyId = defaultMerchant?.companyId;
                        state.defaultMerchant = defaultMerchant;
                    }
                }
                if (addresses?.length) {
                    const addressId = state?.address?.id || addresses?.[0]?.id;
                    state.address = addresses?.find((ad: any) => addressId === ad.id) ?? addresses[0];
                }
                state.userInfo = user;
                state.userResponse = action.payload;
            }
        });
        builder.addCase(myUserInfo.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || null;
        });

        builder.addCase(userUpdateInfo.pending, (state) => {
            state.updateInfoLoading = true;
        });
        builder.addCase(userUpdateInfo.fulfilled, (state, action) => {
            state.updateInfoLoading = false;
        });
        builder.addCase(userUpdateInfo.rejected, (state, action) => {
            state.updateInfoLoading = false;
            state.updateInfoError = action.error.message || null;
        });
        builder.addCase(logoutUser.pending, (state, action) => {
            state.logoutLoading = true;
        });
        builder.addCase(logoutUser.fulfilled, (state, action) => {
            state.logoutLoading = false;
        });
        builder.addCase(logoutUser.rejected, (state, action) => {
            state.logoutLoading = false;
        });
    },
});

export default userSlice.reducer;
